// Lightweight dependency-free SVG charts with value (Y) axes and month (X) labels.
// Scale to their container via viewBox; edge labels are anchored so they don't clip.
import React from 'react';

const W = 680;
const PAD = { l: 52, r: 14, t: 12, b: 26 };
const okv = (v) => v != null && isFinite(v);

function build(values, height) {
  const flat = (values || []).map(Number).filter(okv);
  let maxV = Math.max(0, ...(flat.length ? flat : [0]));
  let minV = Math.min(0, ...(flat.length ? flat : [0]));
  if (maxV === minV) maxV = minV + 1;
  const iH = height - PAD.t - PAD.b;
  const y = (v) => PAD.t + iH * (1 - (((Number(v) || 0) - minV) / (maxV - minV)));
  const ticks = [];
  const N = 4;
  for (let i = 0; i <= N; i++) { const val = minV + ((maxV - minV) * i) / N; ticks.push({ value: val, y: y(val) }); }
  return { y, minV, maxV, ticks };
}

function Axes({ ticks, categories, xAt, height, fmt }) {
  const n = categories.length;
  const step = Math.max(1, Math.ceil(n / 12)); // thin labels so they never overlap
  return (
    <g>
      {ticks.map((t, i) => (
        <g key={'t' + i}>
          <line x1={PAD.l} y1={t.y} x2={W - PAD.r} y2={t.y} stroke={Math.abs(t.value) < 1e-9 ? '#d6d2ca' : '#f1eee8'} strokeWidth="1" />
          <text x={PAD.l - 6} y={t.y + 3} textAnchor="end" fontSize="9" fill="#9a9488">{fmt ? fmt(t.value) : Math.round(t.value)}</text>
        </g>
      ))}
      {categories.map((c, i) => {
        if (i % step !== 0 && i !== n - 1) return null;
        const anchor = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle';
        return <text key={'x' + i} x={xAt(i)} y={height - 8} textAnchor={anchor} fontSize="9" fill="#9a9488">{c}</text>;
      })}
    </g>
  );
}

// Grouped bar chart. categories: string[]; series: [{ name, color, values:number[] }].
export function BarChart({ categories = [], series = [], height = 200, fmt }) {
  const H = height;
  const iW = W - PAD.l - PAD.r;
  const all = series.flatMap((s) => s.values || []);
  const { y, ticks } = build(all, H);
  const zeroY = y(0);
  const n = Math.max(1, categories.length);
  const gw = iW / n;
  const bw = Math.max(3, Math.min(24, (gw * 0.62) / Math.max(1, series.length)));
  const gx = (i) => PAD.l + i * gw + gw / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <Axes ticks={ticks} categories={categories} xAt={gx} height={H} fmt={fmt} />
      {categories.map((c, i) => (
        <g key={i}>
          {series.map((s, si) => {
            const v = Number((s.values || [])[i]) || 0;
            const bx = gx(i) - (series.length * bw) / 2 + si * bw;
            const yv = y(v);
            return <rect key={si} x={bx} y={Math.min(zeroY, yv)} width={Math.max(1, bw - 2)} height={Math.max(0, Math.abs(yv - zeroY))} fill={s.color} rx="1" />;
          })}
        </g>
      ))}
    </svg>
  );
}

// Multi-series line chart. series: [{ name, color, values:number[], fill? }].
export function LineChart({ categories = [], series = [], height = 200, fmt }) {
  const H = height;
  const iW = W - PAD.l - PAD.r;
  const all = series.flatMap((s) => (s.values || []).filter(okv));
  const { y, ticks } = build(all, H);
  const n = Math.max(1, categories.length);
  const xAt = (i) => PAD.l + (n === 1 ? iW / 2 : (iW * i) / (n - 1));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <Axes ticks={ticks} categories={categories} xAt={xAt} height={H} fmt={fmt} />
      {series.map((s, si) => {
        // Connect through gaps: one line through all real points (skip n/a months).
        const pts = (s.values || []).map((v, i) => (okv(v) ? [xAt(i), y(v)] : null)).filter(Boolean);
        const path = pts.map((p) => p[0] + ',' + p[1]).join(' ');
        return (
          <g key={si}>
            {s.fill && pts.length > 1 && (
              <polygon points={`${pts[0][0]},${y(0)} ${path} ${pts[pts.length - 1][0]},${y(0)}`} fill={s.color} opacity="0.10" />
            )}
            {pts.length > 1 && <polyline points={path} fill="none" stroke={s.color} strokeWidth="2" />}
            {(s.values || []).map((v, i) => okv(v) ? <circle key={i} cx={xAt(i)} cy={y(v)} r="2.3" fill={s.color} /> : null)}
          </g>
        );
      })}
    </svg>
  );
}

// Horizontal bars for ranked categories (e.g. top expenses). items: [{label, value}].
export function HBarChart({ items = [], color = '#1a1a2e', fmt }) {
  const max = Math.max(1, ...items.map((it) => Math.abs(it.value)));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
          <div style={{ width: 150, color: '#444', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.label}>{it.label}</div>
          <div style={{ flex: 1, background: '#efece6', borderRadius: 3, height: 14, position: 'relative' }}>
            <div style={{ width: (Math.abs(it.value) / max * 100) + '%', background: color, height: '100%', borderRadius: 3 }} />
          </div>
          <div style={{ width: 90, textAlign: 'right', color: '#1a1a2e', fontVariantNumeric: 'tabular-nums' }}>{fmt ? fmt(it.value) : it.value}</div>
        </div>
      ))}
    </div>
  );
}

export function Legend({ items }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#777' }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: it.color, display: 'inline-block' }} /> {it.name}
        </span>
      ))}
    </div>
  );
}
